import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/ui/PermissionGate';

import { contactService } from '../../services/contactService';
import { Search, Filter, Plus, Download, Mail, Edit } from 'lucide-react';

import { sampleDataService } from '../../services/sampleDataService';

import CustomerFilters from './components/CustomerFilters';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';
import CustomerDrawer from './components/CustomerDrawer';



export default function CustomerManagement() {
  const { profile, userRoles, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    subscriptionStatus: '',
    minRevenue: '',
    maxRevenue: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleCustomers, setSampleCustomers] = useState([]);

  // Check authentication and redirect if not logged in
  useEffect(() => {
    if (!isSuperAdmin() && !profile) {
      navigate('/login');
    }
  }, [profile, navigate]);

  // Real-time customer synchronization
  const transformCustomer = (dbCustomer) => ({
    id: dbCustomer?.id,
    tenantId: dbCustomer?.tenant_id,
    companyName: dbCustomer?.company_name,
    contactPerson: dbCustomer?.contact_person,
    email: dbCustomer?.email,
    phone: dbCustomer?.phone,
    industry: dbCustomer?.industry,
    subscriptionStatus: dbCustomer?.subscription_status,
    annualRevenue: dbCustomer?.annual_revenue,
    communicationPreference: dbCustomer?.communication_preference,
    registrationDate: dbCustomer?.registration_date,
    lastContactDate: dbCustomer?.last_contact_date,
    isActive: dbCustomer?.is_active,
    notes: dbCustomer?.notes,
    createdAt: dbCustomer?.created_at,
    updatedAt: dbCustomer?.updated_at
  });

  const { data: realtimeCustomers, setData: setRealtimeCustomers, isConnected } = useRealtimeEntity(
    'customers',
    customers,
    transformCustomer
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setCustomers(realtimeCustomers);
    }
  }, [realtimeCustomers, isConnected]);

  // Load customers on mount
  useEffect(() => {
    loadCustomers();
  }, [profile?.tenant_id]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');

      // Super Admins see ALL customers across ALL tenants (no filtering)
      // Business Admins and below see only their tenant's customers
      const superAdmin = isSuperAdmin();
      const tenantId = superAdmin ? null : profile?.tenant_id;
      const data = await contactService?.getAll(tenantId, superAdmin);

      // No additional client-side filtering needed - service handles it
      setCustomers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = async (customerId) => {
    try {
      const customerDetails = await contactService?.getById(customerId);
      setSelectedCustomer(customerDetails);
      setDrawerOpen(true);
    } catch (err) {
      setError(err?.message || 'Failed to load customer details');
      console.error('Error loading customer:', err);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev =>
      prev?.includes(customerId)
        ? prev?.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers?.length === filteredCustomers?.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers?.map(c => c?.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCustomers?.length === 0) {
      alert('Please select customers first');
      return;
    }

    try {
      if (action === 'export') {
        // Export to CSV logic would go here
        console.log('Exporting customers:', selectedCustomers);
      } else if (action === 'email') {
        // Bulk email logic would go here
        console.log('Sending email to:', selectedCustomers);
      } else if (action === 'deactivate') {
        await contactService?.bulkUpdate(selectedCustomers, { isActive: false });
        await loadCustomers();
        setSelectedCustomers([]);
      }
    } catch (err) {
      setError(err?.message || 'Bulk action failed');
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setDrawerOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }
    
    try {
      await contactService?.delete(customerId);
      await loadCustomers();
      setSelectedCustomers(prev => prev?.filter(id => id !== customerId));
    } catch (err) {
      setError(err?.message || 'Failed to delete customer');
      console.error('Error deleting customer:', err);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const sampleData = await sampleDataService?.getSampleDataByEntity('customers', profile?.tenant_id);
        setSampleCustomers(sampleData);
      } else {
        setSampleCustomers([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    }
  };

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setDrawerOpen(true);
  };

  const handleSaveCustomer = async () => {
    await loadCustomers();
    setDrawerOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      trial: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.inactive;
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      trial: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors?.[status] || colors?.inactive}`}>
        {status}
      </span>
    );
  };

  // Apply filters
  useEffect(() => {
    // Combine real and sample data if sample data is active
    const allCustomers = showSampleData ? [...customers, ...sampleCustomers] : customers;
    
    let filtered = allCustomers;

    if (searchTerm) {
      filtered = filtered?.filter(c =>
        c?.companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (filters?.industry) {
      filtered = filtered?.filter(c => c?.industry === filters?.industry);
    }

    if (filters?.subscriptionStatus) {
      filtered = filtered?.filter(c => c?.subscriptionStatus === filters?.subscriptionStatus);
    }

    if (filters?.minRevenue) {
      filtered = filtered?.filter(c => c?.annualRevenue >= filters?.minRevenue);
    }

    if (filters?.maxRevenue) {
      filtered = filtered?.filter(c => c?.annualRevenue <= filters?.maxRevenue);
    }

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, filters, showSampleData, sampleCustomers]);

  return (
    <div className="min-h-screen bg-gray-50 p-6 relative">
      <ComponentIdBadge
        id="pg-customer-management-main"
        name="Customer Management Main Page"
        description="Customer database with filtering, bulk actions, and detailed profiles"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
            <p className="text-gray-500 mt-1">Manage your customer database</p>
          </div>
          <PermissionGate feature="customers" level="write">
            <button
              onClick={handleAddCustomer}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>
          </PermissionGate>
        </div>
        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        {/* Search and Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>

            {/* Add Customer */}
            <button
              onClick={handleAddCustomer}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>

            {/* Bulk Actions */}
            {selectedCustomers?.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkAction('export')}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-4 h-4" />
                  Export ({selectedCustomers?.length})
                </button>
                <button
                  onClick={() => handleBulkAction('email')}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
              </div>
            )}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <CustomerFilters
              filters={filters}
              setFilters={setFilters}
              onClose={() => setShowFilters(false)}
            />
          )}
        </div>
        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading customers...</p>
          </div>
        ) : (
          <>
            {/* Customers Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Person
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Industry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCustomers?.map((customer) => (
                      <tr key={customer?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {customer?.companyName}
                          </div>
                          <div className="text-sm text-gray-500">{customer?.contactPerson}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{customer?.email}</div>
                          <div className="text-sm text-gray-500">{customer?.phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{customer?.industry}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(customer?.subscriptionStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {customer?.lastContactDate
                            ? new Date(customer.lastContactDate)?.toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <PermissionGate feature="customers" level="write">
                            <button
                              onClick={() => handleEdit(customer)}
                              className="text-primary hover:text-primary/80 mr-4"
                            >
                              Edit
                            </button>
                          </PermissionGate>
                          <PermissionGate feature="customers" level="delete">
                            <button
                              onClick={() => handleDelete(customer?.id)}
                              className="text-error hover:text-error/80"
                            >
                              Delete
                            </button>
                          </PermissionGate>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{customers?.length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-green-600">
                  {customers?.filter(c => c?.subscriptionStatus === 'active')?.length}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600">Trial Accounts</p>
                <p className="text-2xl font-bold text-blue-600">
                  {customers?.filter(c => c?.subscriptionStatus === 'trial')?.length}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(customers?.reduce((sum, c) => sum + (c?.annualRevenue || 0), 0))}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      {drawerOpen && (
        <CustomerDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          customer={selectedCustomer}
          onSave={handleSaveCustomer}
        />
      )}
    </div>
  );
}